import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, Share2, ImageIcon, Sparkles, Loader2 } from 'lucide-react';
import { findLibraryItem, getItemDetail } from '../data/mock';
import { useKit } from '../hooks/useKit';
import { API } from '../contexts/AuthContext';
import { toast } from 'sonner';

const toneStyles = {
  warn: 'border-l-botanica-warn bg-amber-50',
  safe: 'border-l-botanica-green bg-botanica-greenLight'
};
const toneText = {
  warn: 'text-botanica-warn',
  safe: 'text-botanica-greenDark'
};

export default function LibraryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSaved, toggle } = useKit();
  const [illustration, setIllustration] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setIllustration(null);
    setGenLoading(true);
    const load = async () => {
      try {
        const cached = await axios.get(`${API}/illustration/${encodeURIComponent(id)}`);
        if (active) {
          setIllustration(cached.data.image);
          setGenLoading(false);
        }
        return;
      } catch (e) {
        /* not cached yet -> generate */
      }
      const found = findLibraryItem(id);
      if (!found) {
        if (active) setGenLoading(false);
        return;
      }
      try {
        const res = await axios.post(`${API}/illustration`, {
          item_id: found.item.id, name: found.item.name, latin: found.item.latin, category: found.category
        });
        if (active) setIllustration(res.data.image);
      } catch (e) {
        /* leave placeholder */
      } finally {
        if (active) setGenLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id]);

  const found = findLibraryItem(id);
  if (!found) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center min-h-[60vh]">
        <p className="text-botanica-muted mb-4">We couldn't find that entry.</p>
        <Link to="/library" className="text-botanica-green underline">Back to library</Link>
      </div>
    );
  }

  const { item, category } = found;
  const detail = getItemDetail(item, category);
  const saved = isSaved(item.id);

  const handleSave = () => {
    const added = toggle({ id: item.id, name: item.name, type: 'library', category, subtitle: item.latin });
    toast[added ? 'success' : 'message'](added ? `${item.name} added to your kit` : `${item.name} removed from your kit`);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-6 min-h-[70vh]">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-botanica-card/70 flex items-center justify-center text-botanica-ink hover:bg-botanica-card transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toast.message('Link copied — share Your Herbal Companion with a friend.')}
            className="w-9 h-9 rounded-full bg-botanica-card/70 flex items-center justify-center text-botanica-muted hover:text-botanica-ink transition-colors"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm transition-colors ${saved ? 'bg-botanica-green text-white' : 'bg-botanica-card/70 text-botanica-ink hover:bg-botanica-card'}`}
          >
            <Heart className={`w-4 h-4 ${saved ? 'fill-white' : ''}`} /> {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      {/* Permanent photo */}
      <div className="w-full rounded-3xl overflow-hidden bg-botanica-card/70 border border-botanica-card mb-6">
        {illustration ? (
          <img src={illustration} alt={`Photo of ${item.name}`} className="w-full h-64 md:h-80 object-cover" />
        ) : genLoading ? (
          <div className="h-64 md:h-80 flex flex-col items-center justify-center gap-2 animate-pulse">
            <Loader2 className="w-7 h-7 text-botanica-green animate-spin" />
            <span className="text-sm text-botanica-muted">Loading photo…</span>
          </div>
        ) : (
          <div className="h-64 md:h-80 flex flex-col items-center justify-center gap-2">
            <ImageIcon className="w-7 h-7 text-botanica-muted" />
            <span className="text-sm text-botanica-muted">Photo unavailable</span>
          </div>
        )}
      </div>

      {/* Header */}
      <p className="text-[11px] uppercase tracking-widest text-botanica-green font-semibold mb-1">{category}</p>
      <h1 className="font-serif text-4xl text-botanica-ink mb-1">{item.name}</h1>
      <p className="italic text-botanica-muted mb-4">{item.latin}</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {item.benefits.map((b) => (
          <span key={b} className="text-xs px-3 py-1 rounded-full bg-botanica-card text-botanica-ink">{b}</span>
        ))}
      </div>
      <p className="text-xs text-botanica-muted mb-8">herbal companion · {item.id}</p>

      {/* Aroma */}
      <div className="flex items-center gap-6 py-3 border-t border-botanica-card mb-6">
        <span className="text-sm text-botanica-muted w-20">Aroma</span>
        <span className="text-sm text-botanica-ink">{detail.aroma}</span>
      </div>

      {/* Properties */}
      <div className="mb-8">
        <span className="text-sm text-botanica-muted w-20 inline-block align-top">Notes</span>
        <span className="inline-flex flex-wrap gap-2 ml-2">
          {item.properties.map((p) => (
            <span key={p} className="text-xs px-2.5 py-1 rounded-full border border-botanica-card text-botanica-muted">{p}</span>
          ))}
        </span>
      </div>

      {/* Benefits */}
      <section className="mb-8">
        <h2 className="font-serif text-2xl text-botanica-ink mb-3">Benefits</h2>
        <ul className="space-y-2">
          {item.benefits.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-botanica-ink">
              <span className="w-1.5 h-1.5 rounded-full bg-botanica-green mt-2 shrink-0" /> {b}
            </li>
          ))}
        </ul>
      </section>

      {/* How to use */}
      <section className="mb-8">
        <h2 className="font-serif text-2xl text-botanica-ink mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-botanica-green" /> How to Use
        </h2>
        <ul className="space-y-2">
          {detail.howToUse.map((u) => (
            <li key={u} className="flex items-start gap-2.5 text-sm text-botanica-ink">
              <span className="w-1.5 h-1.5 rounded-full bg-botanica-green mt-2 shrink-0" /> {u}
            </li>
          ))}
        </ul>
      </section>

      {/* Safety boxes */}
      <section className="space-y-3">
        {detail.safetyBoxes.map((box) => (
          <div key={box.label} className={`rounded-2xl border-l-4 px-5 py-4 ${toneStyles[box.tone]}`}>
            <p className={`text-sm font-semibold mb-1 ${toneText[box.tone]}`}>{box.label}</p>
            <p className={`text-sm ${toneText[box.tone]}`}>{box.text}</p>
          </div>
        ))}
      </section>

      <button
        onClick={handleSave}
        className={`w-full mt-8 flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-medium transition-colors ${saved ? 'bg-botanica-card text-botanica-ink' : 'bg-botanica-green text-white hover:bg-botanica-greenDark'}`}
      >
        <Heart className={`w-4 h-4 ${saved ? 'fill-botanica-green text-botanica-green' : ''}`} />
        {saved ? 'Saved to your kit' : 'Add to my kit'}
      </button>
    </div>
  );
}
